import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import moment from 'moment';
import { money } from "../../../services/format";
import { t } from 'ttag';
import useOperatorContext from '../../../contexts/operator/useOperatorContext';
import useOrderContext from '../../../contexts/order/useOrderContext';
import PageSpinnerThree from '../../general/PageSpinnerThree';
import { CTA } from "../../general/Button";
import AppLayout from '../../layouts/app/AppLayout';
import ScrollToTop from '../../../hooks/scrollToTop';
import { useParams } from "react-router-dom";
import { getOrder, postOrderRwg } from "../../../services/api/basket";
import { getDisplayName } from '../../../services/channel';
import { getAiReferral } from '../../../services/aiReferral';
import { firstTime, toOrderItems, track } from '../../../services/analytics';
import { TOrder } from '../../../contexts/order/order-context';
import OrderSummary from "./components/OrderSummary";
import HelpModal from "./components/HelpModal";

const ConfirmationPage = () => {
  const { ref } = useParams();
  const operatorContext = useOperatorContext();
  const orderContext = useOrderContext();
  const navigate = useNavigate();
  const [contextError, setContextError] = useState<string|null>(null);
  const [helpModalShow, setHelpModalShow] = useState(false);
  // the order that was read, to tell Google Analytics of once the operator is known too (see below)
  const [purchased, setPurchased] = useState<TOrder | null>(null);
  const POLLING_INTERVAL = 30 * 1000; // in ms

  const total = () => {
     return orderContext.order?.total ?? 0;
  }

  // Helper function to get a cookie by name
  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';')?.shift();
    return null;
  };

  // Google Ordering conversion tracking
  const googleOrderingPartnerId = process.env.REACT_APP_GOOGLE_ORDERING_PARTNER_ID as string;
  const googleOrderingConversionTrackingEndpoint = process.env.REACT_APP_GOOGLE_ORDERING_CONVERSION_TRACKING_ENDPOINT as string;

  useEffect(() => {
    getOrder(ref)
      .then((data: TOrder | null) => {
        orderContext.setOrder(data);
        orderContext.setStoredOrder(data);
        
    	// Google Ordering conversion tracking
    	const rwgToken = getCookie('__Secure-_rwgToken');
    	const rwgMerchantId = getCookie('__Secure-_rwgMerchantId');
    	const rwgCurrentMerchantId = operatorContext.operator?.handle;
    	const rwgMerchantChanged = (rwgMerchantId && rwgCurrentMerchantId && rwgMerchantId !== rwgCurrentMerchantId) ? '1' : '2';

    	if (rwgToken) {
      		fetch(googleOrderingConversionTrackingEndpoint, {
        		method: "POST",
        		body: JSON.stringify({
          			conversion_partner_id: googleOrderingPartnerId,
          			rwg_token: decodeURIComponent(rwgToken),
          			merchant_changed: rwgMerchantChanged
        		})
      		});

      		// flag the order as started on Google, so HQ can show it (a failure here must not affect the confirmation)
      		postOrderRwg(data?.ref ?? ref ?? '').catch(() => {});
    	}

    	setPurchased(data);
      })
      .catch((reason) => {
        setContextError(reason.message);
      });
    }, []);

  // GA4/GTM purchase event: to Orderswift's property and the operator's, once for the order (not again if it is looked at again), and
  // once the operator is known, as its currency is needed (when this is the first page of the visit it isn't, to begin with)
  useEffect(() => {
    if (!purchased || !operatorContext.operator) return;
    if (!firstTime(`purchase_${purchased.ref}`)) return;

    track('purchase', {
      channel: purchased.channel,
      site_handle: purchased.site?.handle,
      // (set when the visit came from an AI assistant, so an order can be told apart from any other in Google Analytics)
      ai_platform: getAiReferral() ?? undefined,
    }, {
      ecommerce: {
        transaction_id: purchased.ref,
        // (the order is in pence; Google Analytics wants pounds)
        value: (purchased.total ?? 0) / 100,
        currency: operatorContext.operator.ccy,
        shipping: purchased.delivery?.fee ? purchased.delivery.fee / 100 : undefined,
        coupon: purchased.codes?.[0]?.text,
        items: toOrderItems(purchased.basket ?? []),
        handle: operatorContext.operator.handle,
        site_handle: purchased.site?.handle,
      },
    });
  }, [purchased, operatorContext.operator]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      // only poll when the order is already set and the channel is delivery..
      if ( ref && orderContext && ['delivery', 'catering', 'venues'].includes(orderContext.storedOrder?.channel ?? '') ) {
        getOrder(ref)
          .then((data: TOrder | null) => orderContext.setOrder(data))
          .catch((reason) => {
            setContextError(reason.message);
          });
      }
    }, POLLING_INTERVAL);
    return () => clearInterval(intervalId); // Cleanup on component unmount
  }, [ref]);

  if ( contextError ) {
    navigate('/');
    return <PageSpinnerThree show={true} />;
  }

  if (!orderContext || !orderContext.order) {
    return <PageSpinnerThree show={true} />;
  }

  if (orderContext.loading) {
    return <PageSpinnerThree show={true} />;
  }

  let displayName = getDisplayName(orderContext.order?.channel);

  if ( orderContext.order?.channel === 'catering' && operatorContext.operator?.theme.v4.catering_label ) {
      displayName = operatorContext.operator?.theme.v4.catering_label;
  }

  let timeslot = orderContext.order?.ready_time?.name;
  let timeslot_str = '';

  if (timeslot) {
    moment.locale('en', {
        calendar : {
            lastDay : 'LT[,] [Yesterday]',
            sameDay : 'LT[,] [Today]',
            nextDay : 'LT[,] [Tomorrow]',
            lastWeek : 'LT[,] [last] dddd',
            nextWeek : 'LT[,] dddd',
            sameElse : 'LT[,] dddd Do MMMM'
        }
    });

    if (orderContext.order?.ready_time?.is_asap) {
      timeslot_str = timeslot;
    } else {
      timeslot_str = moment(timeslot, 'YYYY-MM-DD HH:mm').calendar();
    }
  }

  return (
    <AppLayout page="confirmation">
      <ScrollToTop />
      <Helmet>
        <title>{operatorContext.operator?.name} - {t`Order confirmation - Powered by Orderswift`}</title>
      </Helmet>
      <StyledContainer>
        <StyledHeaderRow>
          <h1 className="page-header">{t`Thank you`}{ orderContext.order?.customer.first_name && (<>, {orderContext.order?.customer.first_name}!</>) }</h1>
        </StyledHeaderRow>
        <StyledRow>
          <StyledFulfilmentContainer>
            <StyledFulfilment>
              <ContainerHeading first={true}>Confirmed <span className="material-symbols-outlined material-symbols-outlined--filled">check_circle</span></ContainerHeading>
              <div className={`block ${orderContext.order?.channel === 'dine_in' ? 'block--end' : ''}`}>
                <div className="operator-site">{ operatorContext.operator?.name } &middot; { orderContext.order?.site?.name } { orderContext.order?.channel === 'dine_in' && ( <>&middot; Table {orderContext.order?.table}</> ) }</div>
                <div className="order-ref">Reference: { orderContext.order?.ref }</div>
                <div className="total">Total {money(total())}</div>
              </div>
              { orderContext.order?.channel !== 'dine_in' && (
                <>
                  <ContainerHeading>{ displayName }</ContainerHeading>
                  <div className={`block block--end ${operatorContext.operator?.theme.v4.hero_2 ? '' : 'block--end--no-hero'}`}>
                    { orderContext.order?.channel === 'collection' && (
                      <>
                        <div className="address">
                          Collection &middot; <span>
                            <a href={`https://www.google.com/maps/search/${orderContext.order?.site?.address_1}, ${orderContext.order?.site?.address_city}, ${orderContext.order?.site?.address_postcode}/`} target="_blank">{orderContext.order?.site?.address_1}, {orderContext.order?.site?.address_city}, {orderContext.order?.site?.address_postcode}</a>
                            <span className="material-symbols-outlined">open_in_new</span>
                          </span>
                        </div>
                        <div className="timeslot">Collection: { timeslot_str }</div>
                      </>
                    )}
                    { ['delivery', 'catering', 'venues'].includes(orderContext.order.channel) && (
                        <>
                          <div className="address">Delivery &middot; { orderContext.order.channel === 'venues' ? orderContext.order?.delivery?.delivery_venue_name : orderContext.order?.delivery?.destination }</div>
                          <div className="timeslot">{ timeslot_str }</div>
                          { orderContext.order?.delivery?.view == 'delivered' && orderContext.order?.delivery?.estimated && (
                            <ActualDelivery>
                              Delivered: { orderContext.order?.delivery?.estimated } <span className="material-symbols-outlined material-symbols-outlined--filled">check_circle</span>
                            </ActualDelivery>
                          )}
                          { !['delivered', 'cancelled'].includes(orderContext.order?.delivery?.view) && orderContext.order?.delivery?.provider !== 'self' && (
                            <>
                              { orderContext.order?.delivery?.estimated && (
                                <EstimatedDelivery>
                                  Estimated delivery: { orderContext.order?.delivery?.estimated }
                                </EstimatedDelivery>
                              )}
                              { orderContext.order?.delivery?.provider === 'Uber Direct' && (
                                <DeliveryTracking>
                                  { ['scheduled', 'enroute'].includes(orderContext.order?.delivery?.view) && orderContext.order?.delivery?.provider_link && (
                                    <IconCTA
                                      onClick={() => window.open(orderContext.order?.delivery?.provider_link ?? '#', '_blank', 'noopener,noreferrer')}
                                      block
                                    >
                                      <div className={'btn-wrapper'}>
                                        <span className="material-symbols-outlined">open_in_new</span>
                                        Track order
                                      </div>
                                    </IconCTA>
                                  ) }
                                  { !['scheduled', 'enroute'].includes(orderContext.order?.delivery?.view) && (
                                    <IconCTA
                                      onClick={() => {}}
                                      block
                                      disabled
                                    >
                                      <div className={'btn-wrapper'}>
                                        Tracking will appear here
                                      </div>
                                    </IconCTA>
                                  ) }
                                </DeliveryTracking>
                              )}
                              { orderContext.order?.delivery?.code && (
                                <DriverCode>
                                  <span>Give this code to your rider</span> {orderContext.order?.delivery?.code?.split('')?.map((digit, idx) => (<span className="code-digit" key={'digit_' + idx}>{digit}</span>))}
                                </DriverCode>
                              )}
                            </>
                          ) }
                        </>
                    ) }
                    { orderContext.order.site?.show_instructions_at_confirm && orderContext.order.site?.instructions && (
                        <Instructions>
                        { orderContext.order.site?.instructions }
                        </Instructions>
                    ) }
                  </div>
                </>
              )}
            </StyledFulfilment>
            { operatorContext.operator?.theme.v4.hero_2 && ( <HeroImage/> )}
          </StyledFulfilmentContainer>
          <StyledOrderDetails>
            <OrderSummary />
            <HelpModal
              show={helpModalShow}
              onHide={() => setHelpModalShow(false)}
              siteTel={orderContext.order?.site.phone}
              provider={orderContext.order?.delivery?.provider ?? ''}
              providerTel={orderContext.order?.delivery?.provider_tel ?? ''}
              providerLink={orderContext.order?.delivery?.provider_link ?? ''}
            />
            <CTAWrapper>
              <IconCTA
                onClick={() => setHelpModalShow(true)}
                block
                secondary
              >
                <div className={'btn-wrapper'}>
                  <span className="material-symbols-outlined">help</span>
                  Order support
                </div>
              </IconCTA>
            </CTAWrapper>
          </StyledOrderDetails>
        </StyledRow>
      </StyledContainer>
    </AppLayout>
  );
};

export const StyledContainer = styled.div`
  max-width: 1092px;
  padding: 0;
  display: block;
  margin-left: auto;
  margin-right: auto;

  .block {
    margin-bottom: 16px;
    padding: 0 16px 16px 16px;
    border-bottom: 1px solid var(--layouts_container_border);
    font-size: 16px;
    line-height: 24px;
  }

  .block--end {
    margin-bottom: 0;
    border-bottom: none;
  }

  .block--end--no-hero {
    @media screen and (max-width: 992px) {
      border-bottom: 1px solid var(--layouts_container_border);
    }
  }

  .conf {
    &__heading {
      padding: 16px;
      font-family: var(--text_body_font);
      letter-spacing: var(--text_body_font_spacing);
      text-transform: var(--text_body_font_case);
    }

    &__receipt-item {
      display: flex;
      gap: 12px;
      margin-bottom: 12px;
      color: var(--text-black);

      &__name {
        flex-grow: 1;
      }

      &--bold {
        font-weight: 600;
      }
    }
  }
`;

export const StyledHeaderRow = styled.div`
  padding: 24px 0;
  text-align: center;
`;

export const StyledRow = styled.div`
  @media screen and (min-width: 993px) {
    padding-bottom: 24px;
    display: flex;
    gap: 56px;
    align-items: flex-start;
   }

  .basket__order-info {
    &--desktop {
      display: none;

      @media screen and (min-width: 993px) {
        display: block;
      }
    }

    &--mobile {
      @media screen and (min-width: 993px) {
        display: none;
      }
    }
  }
`;

export const StyledOrderDetails = styled.div`
  align-self: flex-start;
  width: 100%;
  margin-bottom: 16px;
  background: var(--layouts_container_bg);

  @media screen and (min-width: 993px) {
    order: 2;
    max-width: 600px;
    border: 1px solid var(--layouts_container_border);
    border-radius: var(--radius_xl);
    position: sticky;
    top: 112px;
  }
`;

export const StyledFulfilmentContainer = styled.div`
  width: 100%;

  @media screen and (min-width: 993px) {
    order: 1;
    max-width: 600px;
  }
`;

export const StyledFulfilment = styled.div`
  width: 100%;
  background: var(--layouts_container_bg);

  @media screen and (min-width: 993px) {
    order: 1;
    max-width: 600px;
    border: 1px solid var(--layouts_container_border);
    border-radius: var(--radius_xl);
  }

  .operator-site {
    font-weight: 700;
    color: var(--text-black);
  }

  .order-ref, .timeslot {
    color: var(--text-grey);
  }

  .address .material-symbols-outlined {
    padding-left: 4px;
    font-size: 24px;
    line-height: 24px;
    vertical-align: bottom;
  }

  .operator-site, .order-ref, .address {
    padding-bottom: 12px;
  }

  .total, .address {
    color: var(--text-black);
  }
`;

const EstimatedDelivery = styled.div`
  padding-top: 12px;
`;

const ActualDelivery = styled.div`
  padding-top: 12px;
`;

const DeliveryTracking = styled.div`
  padding-top: 12px;
`;

const DriverCode = styled.div`
  padding-top: 16px;
  border-top: 1px solid var(--layouts_container_border);

  .code-digit {
    width: 24px;
    height: 24px;
    margin-left: 4px;
    padding: 4px;
    font-size: 16px;
    line-height: 16px;
  }
`;

const Instructions = styled.div`
    margin-top: 12px;
    padding: 16px;
    border-radius: var(--radius_xl);
    background-color: var(--bg-grey);
    color: var(--text-black);
`;

export const CTAWrapper = styled.div`
  padding: 0 16px 16px 16px;
`;

const IconCTA = styled(CTA)`
  .btn-wrapper .material-symbols-outlined {
    padding-right: 4px;
    font-size: 24px;
    vertical-align: bottom;
  }

  @media screen and (max-width: 767px) {
    margin-bottom: 36px;
  }
`;

export const HeroImage = styled.div`
  margin: 0;
  aspect-ratio: 1 / 1;
  width: 100%;
  background-size: cover;
  background-position: center;
  background-image: var(--layouts_hero_2);

  @media screen and (min-width: 993px) {
    max-width: 600px;
    margin: 16px 0 0 0;
    border-radius: var(--radius_xl);
  }
`;

export const ContainerHeading = styled.h2<{first?: boolean}>`
    margin-bottom: 16px;
    ${(props) => props.first ? 'padding: 16px 16px 0 16px;' : 'padding: 0 16px;'}
    font-family: var(--text_body_font);
    letter-spacing: var(--text_body_font_spacing);
    text-transform: var(--text_body_font_case);

    .material-symbols-outlined {
      padding-left: 8px;
      font-size: 24px;
      line-height: 24px;
      vertical-align: bottom;
    }
`;

export default ConfirmationPage;
